import { Component, computed, effect, inject } from '@angular/core';
import { SidebarConfiguration } from '../../../models/sidebar-configuration';
import { SidebarService } from '../../../services/menu/sidebar.service';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { BookmarkCategoryStore } from '../../../stores/bookmark-category/bookmark-category.state';
import { StoryStore } from '../../../stores/story/story.state';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { Bookmark } from '../../../models/bookmark';
import { Story } from '../../../models/story';
import { BookmarkCategory } from '../../../models/bookmark-category';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import {
  MenuItem,
  MenuItemCommandEvent,
  MessageService,
  TreeNode,
} from 'primeng/api';
import { ContextMenuModule } from 'primeng/contextmenu';
import { HeaderComponent } from '../../atoms/header/header.component';
import { MaskComponent } from '../mask/mask.component';
import { SidebarModule } from 'primeng/sidebar';
import { BaseListComponent } from '../base-list/base-list.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { BookmarkListComponent } from '../bookmark-list/bookmark-list.component';
import { RoutingService } from '../../../services/routing.service';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { StateFilter } from '../../../stores/crud.state';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TriStateCheckboxModule } from 'primeng/tristatecheckbox';
import { TagFilterDropdownComponent } from '../../atoms/tag-filter-dropdown/tag-filter-dropdown.component';
import { TopicFilterDropdownComponent } from '../../atoms/topic-filter-dropdown/topic-filter-dropdown.component';
import { DividerModule } from 'primeng/divider';
import { Menu, MenuModule } from 'primeng/menu';
import { PermissionService } from '../../../services/permission.service';
import { checkVisibleMenuItems } from '../../../utility/array.utility';
import { ButtonModule } from 'primeng/button';
import { GlobalsStore } from '../../../stores/globals.state';
import { getState } from '@ngrx/signals';
import { ButtonGroupModule } from 'primeng/buttongroup';
import { BookmarkValidationPanelComponent } from '../bookmark-validation-panel/bookmark-validation-panel.component';
import { ViewChild } from '@angular/core';
import { TreeTableModule } from 'primeng/treetable';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { CommuneStore } from '../../../stores/commune/commune.state';
import { GuestLinkService } from '../../../services/bookmark/guest-link.service';
import { TopicHelperService } from '../../../services/topic/topic-helper.service';
import { BadgeModule } from 'primeng/badge';
import { DebugComponent } from '../../atoms/debug/debug.component';
import { TagStore } from '../../../stores/tag/tag.state';
import { TopicStore } from '../../../stores/topic/topic.state';
import { makeUniqueStringArray } from '../../../utility/array.utility';
import { BookmarkCategoryHelperService } from '../../../services/bookmark-category/bookmark-category-helper.service';

@Component({
  selector: 'app-bookmark-administration',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    ContextMenuModule,
    HeaderComponent,
    MaskComponent,
    SidebarModule,
    FloatLabelModule,
    InputTextModule,
    FormsModule,
    IconFieldModule,
    InputIconModule,
    TriStateCheckboxModule,
    TagFilterDropdownComponent,
    TopicFilterDropdownComponent,
    DividerModule,
    MenuModule,
    ButtonModule,
    ButtonGroupModule,
    BookmarkValidationPanelComponent,
    TreeTableModule,
    TagModule,
    TooltipModule,
    NgClass,
    BadgeModule,
    DebugComponent,
  ],
  templateUrl: './bookmark-administration.component.html',
  styleUrl: './bookmark-administration.component.scss',
})
export class BookmarkAdministrationComponent extends BaseListComponent<BookmarkCategory> {
  readonly store = inject(BookmarkCategoryStore);
  readonly tagStore = inject(TagStore);
  readonly topicStore = inject(TopicStore);
  readonly bookmarkStore = inject(BookmarkStore);
  readonly storyStore = inject(StoryStore);
  readonly activeUserStore = inject(ActiveUserStore);
  readonly globalStore = inject(GlobalsStore);
  readonly bookmarkCategoryHelper = inject(BookmarkCategoryHelperService);

  cols = [
    { width: '30%', field: 'name', header: 'Bezeichnung' },
    {
      width: '22%',
      icon: 'fa fas fa-book-open',
      field: 'topics',
      header: 'Themen',
    },
    {
      width: '22%',
      icon: 'fa fas fa-tags',
      field: 'tags',
      header: 'Schlagworte',
    },
    {
      width: '10%',
      icon: 'fa fas fa-eye',
      field: 'published',
      header: 'Sichtbarkeit',
    },
    { width: '16%', field: 'actions', header: '' },
  ];

  treeTableData = computed(() => {
    const categories = this.store.asSortedArray();
    const storiesGrouped = this.storyStore.groupedByCategory();
    const allBookmarks = this.bookmarkStore.items();
    const filterActive = this.isFilterActive();

    // `BookmarkCategoryHelperService` reads straight from the raw store and
    // knows nothing about `filterQuery` (unlike `StoryStore.groupedByCategory`,
    // which already applies its own filter) - so bookmark results are
    // narrowed down to the currently filtered bookmark set here.
    const filteredBookmarkIds = filterActive
      ? new Set(this.bookmarkStore.filteredItems().map(b => b._id))
      : null;
    const filterBookmarks = (bookmarks: Bookmark[]): Bookmark[] =>
      filteredBookmarkIds
        ? bookmarks.filter(b => filteredBookmarkIds.has(b._id))
        : bookmarks;

    const bookmarksGrouped = this.bookmarkCategoryHelper.getGroupedByCategory();

    const nodes: TreeNode[] = [];

    // Privat section
    const privateBookmarks = filterBookmarks(
      this.bookmarkCategoryHelper.getPrivateBookmarks()
    );
    if (privateBookmarks.length > 0) {
      nodes.push({
        key: 'private',
        data: {
          name: 'Privat',
          topics: [],
          type: 'category',
          private: true,
        },
        children: privateBookmarks.map((b: Bookmark) => ({
          key: b._id,
          data: {
            name: b.title,
            tags: b.tags,
            topics: this.topicHelper.getTopicsByBookmark(b),
            published: b.published,
            type: 'bookmark',
            item: b,
          },
        })),
        expanded:
          filterActive || (this.cachedExpandedStates['private'] ?? false),
      });
    }

    // Unsorted section
    const unsortedBookmarks = filterBookmarks(
      this.bookmarkCategoryHelper.getUnsortedBookmarks()
    );
    if (unsortedBookmarks.length > 0) {
      nodes.push({
        key: 'unsorted',
        data: {
          name: 'nicht zugeordnet',
          topics: [],
          type: 'category',
          unsorted: true,
        },
        children: unsortedBookmarks.map((b: Bookmark) => ({
          key: b._id,
          data: {
            name: b.title,
            tags: b.tags,
            topics: this.topicHelper.getTopicsByBookmark(b),
            published: b.published,
            type: 'bookmark',
            item: b,
          },
        })),
        expanded:
          filterActive || (this.cachedExpandedStates['unsorted'] ?? false),
      });
    }

    // Regular categories
    categories.forEach(cat => {
      const catBookmarks = filterBookmarks(bookmarksGrouped[cat._id] || []);
      const catStories = storiesGrouped[cat._id] || [];

      // Hide categories with no matches while a filter is active
      if (
        filterActive &&
        catBookmarks.length === 0 &&
        catStories.length === 0
      ) {
        return;
      }

      const children: TreeNode[] = [];

      // Add Bookmarks. Keyed as `${categoryId}-${bookmarkId}`, not just the
      // bookmark's own id, since a bookmark can now belong to several
      // categories and would otherwise produce duplicate node keys across
      // branches of the same tree.
      catBookmarks.forEach(b => {
        children.push({
          key: `${cat._id}-${b._id}`,
          data: {
            name: b.title,
            tags: b.tags,
            topics: this.topicHelper.getTopicsByBookmark(b),
            published: b.published,
            type: 'bookmark',
            item: b,
          },
        });
      });

      // Add Stories
      catStories.forEach(s => {
        const storyBookmarks = (s.bookmarks || [])
          .map(id => allBookmarks[id])
          .filter(b => !!b);

        children.push({
          key: s._id,
          data: {
            name: s.title,
            tags: s.tags,
            topics: this.topicHelper.getTopicsByStory(s),
            published: s.published,
            type: 'story',
            item: s,
          },
          children: storyBookmarks.map(b => ({
            key: `${s._id}-${b._id}`,
            data: {
              name: b.title,
              tags: b.tags,
              topics: this.topicHelper.getTopicsByBookmark(b),
              type: 'bookmark',
              item: b,
            },
          })),
          expanded: filterActive || (this.cachedExpandedStates[s._id] ?? false),
        });
      });

      nodes.push({
        key: cat._id,
        data: {
          icon: 'fas fa-layer-group',
          name: cat.title,
          tags: this.topicHelper.getBookmarkCategoryTags(cat._id),
          topics: this.topicHelper.getTopicsByBookmarkCategoryId(cat._id),
          type: 'category',
          item: cat,
        },
        children: children,
        expanded: filterActive || (this.cachedExpandedStates[cat._id] ?? false),
      });
    });

    return nodes;
  });

  invalidBookmarks = computed(() =>
    String(this.globalStore.bookmarkValidation()?.invalidBookmarks || '')
  );

  @ViewChild(BookmarkValidationPanelComponent)
  validationPanel!: BookmarkValidationPanelComponent;

  override sidebarId = 'bookmark-administration';

  atlasNavigation: string[] = [];
  filterQuery: StateFilter = {
    query: '',
    category: [],
    locked: null,
    published: null,
    tags: [],
    products: [],
    ids: undefined,
  };
  selectedTopics: string[] = [];
  rowMenuItems: MenuItem[] = [];

  localStorage: Storage | undefined;
  cachedExpandedStates: { [key: string]: boolean } = {};
  private static readonly EXPANDED_STATE_KEY = 'bookmarkTreeExpandedState';

  constructor(
    protected sidebarService: SidebarService,
    router: Router,
    route: ActivatedRoute,
    messageService: MessageService,
    confirmationDialogService: ConfirmationDialogService,
    routingService: RoutingService,
    private permissionService: PermissionService,
    private topicHelper: TopicHelperService
  ) {
    super(
      router,
      route,
      messageService,
      confirmationDialogService,
      routingService
    );

    this.localStorage = document.defaultView?.localStorage;
    this.cachedExpandedStates = JSON.parse(
      this.localStorage?.getItem(
        BookmarkAdministrationComponent.EXPANDED_STATE_KEY
      ) || '{}'
    );

    effect(() => {
      const globalState = getState(this.globalStore);
      this.atlasNavigation =
        globalState.activeCommune?.atlasConfig?.navigation || [];
    });

    effect(() => {
      const config = this.sidebarConfig();
      queueMicrotask(() => {
        this.sidebarService.setConfiguration(config);
      });
    });
  }

  /**
   * Updates the filter of the bookmark store with the current filter query.
   * Applies the update operation with a short delay using a timeout to ensure appropriate filtering.
   *
   * @return {void} Does not return a value.
   */
  updateFilter(): void {
    setTimeout(() => {
      this.bookmarkStore.updateFilter(this.filterQuery);
      this.storyStore.updateFilter(this.filterQuery);
    }, 10);
  }

  /**
   * Derives the bookmark categories belonging to the selected topics and applies
   * them as the filter, since bookmarks/stories only reference topics
   * indirectly through their bookmark category.
   *
   * Sets two things on the shared filter: `category` (still interpreted
   * directly by `StoryStore`, which keeps its own single `category` field)
   * and `ids` (the bookmark ids those categories actually contain, resolved
   * via `BookmarkCategoryHelperService` - `BookmarkStore` no longer has a
   * `category` field of its own to filter by, now that the relationship
   * lives on `BookmarkCategory.bookmarks`).
   *
   * @param {string[]} topicIds - The currently selected topic ids.
   * @return {void} Does not return a value.
   */
  onTopicsChange(topicIds: string[]): void {
    this.selectedTopics = topicIds;
    const categoryIds = makeUniqueStringArray(
      (topicIds || []).flatMap(
        topicId => this.topicStore.getById(topicId)?.bookmarkCategories || []
      )
    );
    this.filterQuery.category = categoryIds;
    this.filterQuery.ids = makeUniqueStringArray(
      categoryIds.flatMap(categoryId =>
        this.bookmarkCategoryHelper
          .getBookmarksForCategory(categoryId)
          .map(bookmark => bookmark._id)
      )
    );
    this.updateFilter();
  }

  /**
   * Whether any filter (search, tags, topics/category or visibility) is currently active.
   *
   * @return {boolean} True if at least one filter criterion is set.
   */
  isFilterActive(): boolean {
    const f = this.filterQuery;
    return !!(
      f.query ||
      (f.tags && f.tags.length > 0) ||
      (f.category && f.category.length > 0) ||
      f.published !== null
    );
  }

  /**
   * Persists the expanded/collapsed state of a tree node so it survives the
   * tree being rebuilt after an action (e.g. toggling a bookmark's visibility).
   *
   * @param {any} event - The onNodeExpand/onNodeCollapse event, carrying the affected node.
   * @param {boolean} isExpanded - Whether the node was expanded or collapsed.
   * @return {void} Does not return a value.
   */
  onNodeToggle(event: any, isExpanded: boolean): void {
    const key = event?.node?.key;
    if (!key) {
      return;
    }
    this.cachedExpandedStates[key] = isExpanded;
    this.localStorage?.setItem(
      BookmarkAdministrationComponent.EXPANDED_STATE_KEY,
      JSON.stringify(this.cachedExpandedStates)
    );
  }

  /**
   * Adds the given tag to the active tag filter, if it isn't already part of it.
   *
   * @param {string} tagId - The id of the tag to filter by.
   * @return {void} Does not return a value.
   */
  filterByTag(tagId: string): void {
    const currentTags = this.filterQuery.tags || [];
    if (currentTags.includes(tagId)) {
      return;
    }
    this.filterQuery.tags = [...currentTags, tagId];
    this.updateFilter();
  }

  /**
   * Adds the given topic to the active topic filter, if it isn't already part
   * of it, reusing `onTopicsChange` to derive the resulting category filter.
   *
   * @param {string} topicId - The id of the topic to filter by.
   * @return {void} Does not return a value.
   */
  filterByTopic(topicId: string): void {
    if (this.selectedTopics.includes(topicId)) {
      return;
    }
    this.selectedTopics = [...this.selectedTopics, topicId];
    this.onTopicsChange(this.selectedTopics);
  }

  /**
   * Sets the visibility filter to the given published state.
   *
   * @param {boolean} published - Whether to filter for published (öffentlich) or internal items.
   * @return {void} Does not return a value.
   */
  filterByVisibility(published: boolean): void {
    this.filterQuery.published = published;
    this.updateFilter();
  }

  /**
   * Determines whether the current user may perform the given action on a bookmark.
   *
   * Private and "nicht zugeordnet" bookmarks have no category, so there's no
   * workspace-based category permission to check against - `checkRights` would
   * always deny those, even for the bookmark's own creator. In that case, fall
   * back to ownership; categorized bookmarks keep going through the normal
   * category-based permission check, granted if the user has rights on ANY of
   * the bookmark's categories.
   *
   * @param {Bookmark} bookmark - The bookmark to check.
   * @param {'edit'|'read'|'remove'|'lock'|'unlock'} right - The action to check.
   * @return {boolean} True if the user may perform the action.
   */
  canManageBookmark(
    bookmark: Bookmark,
    right: 'edit' | 'read' | 'remove' | 'lock' | 'unlock'
  ): boolean {
    const categoryIds = this.bookmarkCategoryHelper.getCategoriesForBookmark(
      bookmark._id
    );
    if (categoryIds.length) {
      return this.checkRights(categoryIds, right);
    }
    return bookmark.createdBy === this.activeUserStore.user()?._id;
  }

  /**
   * Determines whether the current user may edit the given row by clicking it,
   * mirroring the visibility rules of the row's own "bearbeiten" menu item.
   * Pseudo-category rows ("Privat"/"nicht zugeordnet") have no backing entity
   * and are therefore never editable.
   *
   * @param {any} rowData - The tree table row data (category, bookmark or story node).
   * @return {boolean} True if clicking the row should open its edit form.
   */
  canEditRow(rowData: any): boolean {
    if (!rowData.item) return false;
    if (rowData.type === 'category') return true;
    if (rowData.type === 'bookmark')
      return this.canManageBookmark(rowData.item, 'edit');
    if (rowData.type === 'story')
      return this.checkRights(rowData.item.category, 'edit');
    return false;
  }

  /**
   * Opens the edit form for the row, same as its "bearbeiten" menu entry.
   *
   * Ignores clicks that originated from one of the row's own interactive
   * controls (topic/tag/visibility pills, the expand toggler, the actions
   * button/menu) - those already handle themselves and call
   * `stopPropagation()`, but this target-based check is a safety net so a
   * stray bubble never re-triggers the edit form on top of the control's own
   * action.
   *
   * @param {any} rowData - The tree table row data (category, bookmark or story node).
   * @param {MouseEvent} event - The click event, used to detect clicks on inner controls.
   * @return {void} Does not return a value.
   */
  onRowClick(rowData: any, event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (
      target.closest(
        '.p-tag, .p-treetable-toggler, .p-button, .p-menu, .p-badge'
      )
    ) {
      return;
    }
    if (!this.canEditRow(rowData)) return;
    if (rowData.type === 'category') this.editBookmarkCategory(rowData.item);
    else if (rowData.type === 'bookmark') this.editBookmark(rowData.item);
    else if (rowData.type === 'story') this.editStory(rowData.item);
  }

  /**
   * Builds and opens the row action menu for the given row.
   *
   * @param {MouseEvent} event - The click event that triggered the menu.
   * @param {any} rowData - The tree table row data (category, bookmark or story node).
   * @param {Menu} menu - The popup menu instance to open.
   * @return {void} Does not return a value.
   */
  openMenu(event: MouseEvent, rowData: any, menu: Menu): void {
    event.stopPropagation();
    this.rowMenuItems = checkVisibleMenuItems(this.buildRowMenuItems(rowData));
    queueMicrotask(() => menu.toggle(event));
  }

  private buildRowMenuItems(rowData: any): MenuItem[] {
    if (rowData.type === 'category' && rowData.item) {
      const category = rowData.item as BookmarkCategory;
      return [
        {
          label: 'Kategorie bearbeiten',
          icon: 'fas fa-pen',
          visible: true,
          command: () => this.editBookmarkCategory(category),
        },
        {
          label: 'Kategorie löschen',
          icon: 'fas fa-trash',
          visible: true,
          command: (event: MenuItemCommandEvent) =>
            this.deleteBookmarkCategory(
              event.originalEvent as MouseEvent,
              category
            ),
        },
      ];
    }

    if (rowData.type === 'bookmark') {
      const bookmark = rowData.item as Bookmark;
      return [
        {
          label: 'Bearbeiten',
          icon: 'fas fa-pen',
          visible: this.canManageBookmark(bookmark, 'edit'),
          command: () => this.editBookmark(bookmark),
        },
        {
          label: 'Öffnen',
          icon: 'fas fa-arrow-up-right-from-square',
          visible: this.canManageBookmark(bookmark, 'read'),
          command: () => this.openBookmark(bookmark),
        },
        {
          label: 'Link kopieren',
          icon: 'fas fa-share',
          visible:
            this.canManageBookmark(bookmark, 'read') && !bookmark.private,
          command: () => this.copyBookmarkLink(bookmark, bookmark.published),
        },
        {
          label: 'Veröffentlichen',
          icon: 'fas fa-earth-europe',
          visible:
            this.canManageBookmark(bookmark, 'unlock') && !bookmark.published,
          command: () => this.publishBookmark(bookmark),
        },
        {
          label: 'Intern schalten',
          icon: 'fas fa-house-user',
          visible:
            this.canManageBookmark(bookmark, 'lock') && bookmark.published,
          command: () => this.lockBookmark(bookmark),
        },
        {
          label: 'Löschen',
          icon: 'fas fa-trash',
          visible: this.canManageBookmark(bookmark, 'remove'),
          command: (event: MenuItemCommandEvent) =>
            this.deleteBookmark(event.originalEvent, bookmark),
        },
      ];
    }

    if (rowData.type === 'story') {
      const story = rowData.item as Story;
      return [
        {
          label: 'Bericht bearbeiten',
          icon: 'fas fa-pen',
          visible: this.checkRights(story.category, 'edit'),
          command: () => this.editStory(story),
        },
        {
          label: 'Bericht öffnen',
          icon: 'fas fa-arrow-up-right-from-square',
          visible: this.checkRights(story.category, 'read'),
          command: () => this.openStory(story, story.published),
        },
        {
          label: 'Veröffentlichen',
          icon: 'fas fa-earth-europe',
          visible:
            this.checkRights(story.category, 'unlock') && !story.published,
          command: () => this.publishStory(story),
        },
        {
          label: 'Intern schalten',
          icon: 'fas fa-house-user',
          visible: this.checkRights(story.category, 'lock') && story.published,
          command: () => this.lockStory(story),
        },
        {
          label: 'Bericht löschen',
          icon: 'fas fa-trash',
          visible: this.checkRights(story.category, 'remove'),
          command: (event: MenuItemCommandEvent) =>
            this.deleteStory(event.originalEvent, story),
        },
      ];
    }

    return [];
  }

  // --- Bookmark Actions ---

  editBookmark(bookmark: Bookmark): void {
    this.router.navigate(
      [
        {
          outlets: {
            bearbeiten: [bookmark._id],
          },
        },
      ],
      { relativeTo: this.route }
    );
  }

  deleteBookmark(event: any, bookmark: Bookmark): void {
    this.confirmationDialogService.confirmDeletion(
      event,
      bookmark.title,
      this.bookmarkStore.entityType(),
      () => {
        this.bookmarkStore.deleteItem(bookmark);
      }
    );
  }

  openBookmark(bookmark: Bookmark, published = false): void {
    const link = this.getBookmarkLink(bookmark, published);
    window.open(link, '_blank');
  }

  getBookmarkLink(data: Bookmark, published: boolean): string {
    return this.routingService.getAbsoluteBookmarkLink(
      data._id,
      published,
      this.activeUserStore.commune()
    );
  }

  copyBookmarkLink(data: Bookmark, published: boolean): void {
    const link = this.getBookmarkLink(data, published);
    navigator.clipboard
      .writeText(link)
      .then(() => {
        this.messageService.add({
          severity: 'info',
          summary: 'Ansicht-Link in die Zwischenablage kopiert',
          life: 3000,
        });
      })
      .catch(() => {
        this.messageService.add({
          severity: 'error',
          summary: 'Fehler beim Kopieren des Links',
          life: 3000,
        });
      });
  }

  /**
   * Sets the published state of a bookmark/story and persists it, showing the
   * matching success toast. Shared by the bookmark and story publish/lock
   * actions, which only differ in the store to persist to and the label used
   * in the toast.
   */
  private setPublished<T extends { published: boolean }>(
    item: T,
    store: { update: (item: T) => void },
    published: boolean,
    entityLabel: string
  ): void {
    item.published = published;
    store.update(item);
    this.messageService.add({
      severity: 'info',
      summary: `${entityLabel} erfolgreich ${published ? 'veröffentlicht' : 'gesperrt'}`,
      life: 3000,
    });
  }

  publishBookmark(bookmark: Bookmark): void {
    this.setPublished(bookmark, this.bookmarkStore, true, 'Ansicht');
  }

  lockBookmark(bookmark: Bookmark): void {
    this.setPublished(bookmark, this.bookmarkStore, false, 'Ansicht');
  }

  // --- Story Actions ---

  editStory(story?: Story): void {
    this.router.navigate(
      [
        {
          outlets: {
            bearbeiten: ['bericht', story?._id || 'neu'],
          },
        },
      ],
      { relativeTo: this.route }
    );
  }

  deleteStory(event: any, story: Story): void {
    this.confirmationDialogService.confirmDeletion(
      event,
      story.title,
      this.storyStore.entityType(),
      () => {
        this.storyStore.deleteItem(story);
      }
    );
  }

  openStory(story: Story, published = false): void {
    const link = this.getStoryLink(story, published);
    window.open(link, '_blank');
  }

  getStoryLink(data: Story, published: boolean): string {
    return published
      ? `${window.location.origin}/atlas/${this.activeUserStore.commune()}/bericht/${data._id}`
      : `${window.location.origin}/atlas-intern/bericht/${data._id}`;
  }

  publishStory(story: Story): void {
    this.setPublished(story, this.storyStore, true, 'Bericht');
  }

  lockStory(story: Story): void {
    this.setPublished(story, this.storyStore, false, 'Bericht');
  }

  checkRights(
    id: string | string[] | undefined,
    right: 'edit' | 'read' | 'remove' | 'lock' | 'unlock'
  ): boolean {
    return this.permissionService.checkBookmarkRights(id || '', right);
  }

  /**
   * Navigates to the route for editing a bookmark category. If a specific bookmark category
   * is provided, it navigates to the editor for that category; otherwise, it navigates to
   * create a new category.
   *
   * @param {BookmarkCategory | undefined} bookmarkCategory - The bookmark category to edit, or undefined to create a new category.
   * @return {void} This method does not return a value.
   */
  editBookmarkCategory(bookmarkCategory: BookmarkCategory | undefined): void {
    this.router.navigate(
      [
        {
          outlets: {
            bearbeiten: [
              'bereich',
              bookmarkCategory ? bookmarkCategory._id : 'neu',
            ],
          },
        },
      ],
      { relativeTo: this.route }
    );
  }

  /**
   * Deletes a bookmark category after user confirmation.
   *
   * @param {MouseEvent} event - The event associated with the delete action.
   * @param {BookmarkCategory} bookmarkCategory - The bookmark category to be deleted.
   * @return {void} This method does not return a value.
   */
  deleteBookmarkCategory(
    event: MouseEvent,
    bookmarkCategory: BookmarkCategory
  ): void {
    this.store.singleSelection(bookmarkCategory._id);

    setTimeout(() => {
      this.confirmationDialogService.confirmBookmarkCategoryDeletion(
        event as Event,
        bookmarkCategory,
        () => this.remove(bookmarkCategory)
      );
    });
  }

  /**
   * Opens the bookmark validation panel
   */
  openValidationPanel(): void {
    if (this.validationPanel) {
      this.validationPanel.open();
    }
  }

  sidebarConfig = computed<SidebarConfiguration>(() => ({
    cssClasses: 'w-12',
    icon: 'fas fa-note-sticky',
    headerText: 'Ansichten',
    subMenu: [
      {
        styleClass: '',
        label: 'Themen einstellen',
        icon: 'fas fa-book-open',
        severity: 'secondary',
        visible: this.activeUserStore.canAccessConfig(),
        routerLink: [
          '/data-backend',
          {
            outlets: {
              overlay: ['themen'],
            },
          },
        ],
      },
      {
        label: 'Gast-Links',
        icon: 'fas fa-link',
        severity: 'secondary',
        visible: this.activeUserStore.canAccessConfig(),
        routerLink: [
          '/data-backend',
          {
            outlets: {
              overlay: ['konfiguration', { outlets: { panel: 'links' } }],
            },
          },
        ],
      },
      {
        label: 'Validierung',
        icon: 'fas fa-file-circle-check',
        severity: 'secondary',
        visible: this.activeUserStore.canAccessEntityAdder(),
        badge: this.invalidBookmarks(),
        routerLink: [
          '/data-backend',
          {
            outlets: {
              overlay: [
                'lesezeichen',
                { outlets: { bearbeiten: ['validierung', 'list'] } },
              ],
            },
          },
        ],
      },
    ],
  }));
}
